import { V6Client } from "@aws-amplify/api-graphql";
import {
  AppointmentFileReferenceStatus,
  AppointmentFileReferencesByAppointmentIDQuery,
  AppointmentType,
  FileProvider,
  FileType,
  GetUploadedFileQuery,
  HistoryTranscriptsByUploadedFileIDQuery,
} from "~/api/API";
import {
  createAppointmentFileReference,
  createHistoryTranscript,
  deleteAppointmentFileReference,
  deleteHistoryTranscript,
} from "~/api/graphql/mutations";
import {
  appointmentFileReferencesByAppointmentID,
  appointmentFileReferencesByUploadedFileID,
  historyTranscriptsByUploadedFileID,
} from "~/api/graphql/queries";
import {
  createUploadedFileOptimized,
  deleteUploadedFileReturningID,
  updateUploadedFileFileNameReturningID,
  updateUploadedFileProviderReturningID,
  updateUploadedFileTypeReturningID,
} from "~/api/graphql-custom/mutations";
import { getUploadedFileOptimized } from "~/api/graphql-custom/queries";
import { REEVALUATION_TYPES } from "~/common/appointmentConstants";
import { errorReporter } from "~/error-handling/errorReporter";
import { amplifyGraphQLClient } from "~/lib/amplify-client";

const RESCHEDULE_INHERITED_READ_ONLY_FILE_TYPES: FileType[] = [
  FileType.QME_NOTIFICATION_FORM,
  FileType.APPOINTMENT_NOTICE,
  FileType.LATE_CANCEL_NOTICE,
  FileType.LATE_RESCHEDULE_NOTICE,
];

/**
 * Parameters for determining the inherited file status when creating
 * AppointmentFileReference records for child appointments.
 */
export interface InheritedFileStatusParams {
  /** Whether this is a reschedule (source has rescheduledToAppointmentID pointing to dest) */
  isReschedule: boolean;
  /** The appointment type of the destination (child) appointment */
  destAppointmentType: AppointmentType;
  /** The file type being inherited */
  fileType: FileType;
  /** The status of the file reference in the source appointment */
  sourceFileStatus: AppointmentFileReferenceStatus;
}

/**
 * Determines the AppointmentFileReferenceStatus for a file being inherited
 * by a child appointment based on the source/dest appointment types,
 * file type, and the source file's current status.
 *
 * This function encapsulates the business logic for file inheritance:
 * - Files that were already INHERITED_READ_ONLY stay read-only
 * - ORIGINAL or INHERITED_WRITEABLE files may become read-only or writeable
 *   depending on the appointment type transition
 */
export function getInheritedFileStatus(
  params: InheritedFileStatusParams,
): AppointmentFileReferenceStatus {
  const { isReschedule, destAppointmentType, fileType, sourceFileStatus } =
    params;

  // Files that are already INHERITED_READ_ONLY should stay read-only
  if (sourceFileStatus === AppointmentFileReferenceStatus.INHERITED_READ_ONLY) {
    return AppointmentFileReferenceStatus.INHERITED_READ_ONLY;
  }

  // For reschedules, files remain writeable so the rescheduled appointment
  // retains ownership of the same medical records.
  if (isReschedule) {
    // Certain file types should not be inherited as writeable on reschedule
    if (RESCHEDULE_INHERITED_READ_ONLY_FILE_TYPES.includes(fileType)) {
      return AppointmentFileReferenceStatus.INHERITED_READ_ONLY;
    }
    return AppointmentFileReferenceStatus.INHERITED_WRITEABLE;
  }

  // Re-evaluations: files from original appointment are read-only
  // (QME->QME_REEVALUATION, AME->AME_REEVALUATION, etc.)
  if (REEVALUATION_TYPES.has(destAppointmentType)) {
    return AppointmentFileReferenceStatus.INHERITED_READ_ONLY;
  }

  // Supplementals: files from original appointment are read-only
  if (destAppointmentType === AppointmentType.SUPPLEMENTAL) {
    return AppointmentFileReferenceStatus.INHERITED_READ_ONLY;
  }

  // Depositions: files from original appointment are read-only
  if (destAppointmentType === AppointmentType.DEPOSITION) {
    return AppointmentFileReferenceStatus.INHERITED_READ_ONLY;
  }

  // Default: inherited files are read-only for safety
  return AppointmentFileReferenceStatus.INHERITED_READ_ONLY;
}

export interface CreateUploadedFileParams {
  /** S3 key for the uploaded file */
  key: string;
  /** Original file name */
  fileName: string;
  /** File type classification */
  type: FileType;
  /** File provider/source */
  provider: FileProvider;
  /** Name of the user who uploaded the file */
  uploadedBy: string;
  /** ID of the source appointment */
  sourceAppointmentId: string;
  /** Size in bytes of the S3 object at `key` */
  fileSizeBytes: number;
}

export interface CreateUploadedFileResult {
  id: string;
  key: string;
  type: FileType;
  provider: FileProvider;
  uploadedAt: string;
  uploadedBy: string;
}

/**
 * An uploaded file with its reference status from AppointmentFileReference.
 * This combines the UploadedFile data with the status indicating whether
 * the file is ORIGINAL, INHERITED_WRITEABLE, or INHERITED_READ_ONLY for
 * the specific appointment context.
 */
export interface UploadedFileWithReferenceStatus {
  /** Unique identifier of the uploaded file */
  id: string;
  /** S3 key for the file */
  key: string | null;
  /** Original file name as stored on the UploadedFile record */
  fileName: string | null;
  /** File type classification */
  type: FileType | null;
  /** File provider/source */
  provider: FileProvider | null;
  /** Name of the user who uploaded the file */
  uploadedBy: string | null;
  /** ISO timestamp when the file was uploaded */
  uploadedAt: string | null;
  /** Size in bytes of the S3 object; null on rows not yet backfilled */
  fileSizeBytes: number | null;
  /** ID of the source appointment where this file was originally uploaded */
  sourceAppointmentId: string | null;
  /** The status of this file reference for the queried appointment */
  referenceStatus: AppointmentFileReferenceStatus;
  /** ID of the AppointmentFileReference record */
  referenceId: string;
}

/**
 * Parameters for retrieving files for an appointment.
 */
export interface GetFilesByAppointmentParams {
  /** The ID of the appointment to get files for */
  appointmentId: string;
}

/**
 * Consolidated service for all uploadedFile GraphQL operations.
 */
export class UploadedFileService {
  private static instance: UploadedFileService;
  private readonly client: V6Client;

  private constructor(client: V6Client) {
    this.client = client;
  }

  static getInstance(client?: V6Client): UploadedFileService {
    if (!UploadedFileService.instance) {
      UploadedFileService.instance = new UploadedFileService(
        client || amplifyGraphQLClient,
      );
    }
    return UploadedFileService.instance;
  }

  async createUploadedFile(
    params: CreateUploadedFileParams,
  ): Promise<CreateUploadedFileResult> {
    const uploadedAt = new Date().toISOString();

    const response = await this.client.graphql({
      query: createUploadedFileOptimized,
      variables: {
        input: {
          key: params.key,
          fileName: params.fileName,
          type: params.type,
          provider: params.provider,
          uploadedAt,
          uploadedBy: params.uploadedBy,
          sourceAppointmentID: params.sourceAppointmentId,
          fileSizeBytes: params.fileSizeBytes,
        },
      },
    });

    if (response.errors) {
      throw new Error(
        `Failed to create uploaded file: ${response.errors.map((e) => e.message).join(", ")}`,
      );
    }

    const uploadedFile = response.data?.createUploadedFile;
    if (!uploadedFile?.id) {
      throw new Error("Failed to create uploaded file: no ID returned");
    }

    // Create AppointmentFileReference to link file to the source appointment
    const fileRefResponse = await this.client.graphql({
      query: createAppointmentFileReference,
      variables: {
        input: {
          appointmentID: params.sourceAppointmentId,
          uploadedFileID: uploadedFile.id,
          status: AppointmentFileReferenceStatus.ORIGINAL,
        },
      },
    });

    if (fileRefResponse.errors) {
      // Log but don't throw - the file was created successfully
      console.error(
        "Failed to create appointment file reference:",
        fileRefResponse.errors.map((e) => e.message).join(", "),
      );
    }

    if (params.type === FileType.HISTORY_TRANSCRIPT) {
      await this.createHistoryTranscriptRow(uploadedFile.id);
    }

    return {
      id: uploadedFile.id,
      key: uploadedFile.key ?? params.key,
      type: (uploadedFile.type as FileType) ?? params.type,
      provider: (uploadedFile.provider as FileProvider) ?? params.provider,
      uploadedAt: uploadedFile.uploadedAt ?? uploadedAt,
      uploadedBy: uploadedFile.uploadedBy ?? params.uploadedBy,
    };
  }

  async updateFileType(id: string, type: FileType): Promise<void> {
    const response = await this.client.graphql({
      query: updateUploadedFileTypeReturningID,
      variables: {
        input: { id, type },
      },
    });

    if (response.errors) {
      throw new Error(
        `Failed to update file type: ${response.errors.map((e) => e.message).join(", ")}`,
      );
    }

    await this.syncHistoryTranscriptRow(id, type);
  }

  async updateFileProvider(id: string, provider: FileProvider): Promise<void> {
    const response = await this.client.graphql({
      query: updateUploadedFileProviderReturningID,
      variables: {
        input: { id, provider },
      },
    });

    if (response.errors) {
      throw new Error(
        `Failed to update file provider: ${response.errors.map((e) => e.message).join(", ")}`,
      );
    }
  }

  async updateFileName(id: string, fileName: string): Promise<void> {
    const response = await this.client.graphql({
      query: updateUploadedFileFileNameReturningID,
      variables: {
        input: { id, fileName },
      },
    });

    if (response.errors) {
      throw new Error(
        `Failed to update file name: ${response.errors.map((e) => e.message).join(", ")}`,
      );
    }
  }

  async deleteFile(id: string): Promise<void> {
    // Clean up the HistoryTranscript side-table row (if any) before the
    // UploadedFile itself — otherwise we'd leave an orphan row pointing at
    // a file that no longer exists.
    const htIds = await this.findHistoryTranscriptIds(id);
    await this.deleteHistoryTranscriptRowsByIds(htIds);

    // First, find and delete all AppointmentFileReference records for this file
    const fileRefsResponse = await this.client.graphql({
      query: appointmentFileReferencesByUploadedFileID,
      variables: {
        uploadedFileID: id,
      },
    });

    if (!fileRefsResponse.errors) {
      const fileRefs =
        fileRefsResponse.data?.appointmentFileReferencesByUploadedFileID
          ?.items ?? [];
      const deletePromises = fileRefs
        .filter(
          (fileRef): fileRef is NonNullable<typeof fileRef> =>
            fileRef?.id != null,
        )
        .map(async (fileRef) => {
          const deleteRefResponse = await this.client.graphql({
            query: deleteAppointmentFileReference,
            variables: {
              input: { id: fileRef.id },
            },
          });
          if (deleteRefResponse.errors) {
            console.error(
              `Failed to delete appointment file reference ${fileRef.id}:`,
              deleteRefResponse.errors.map((e) => e.message).join(", "),
            );
          }
        });
      await Promise.all(deletePromises);
    } else {
      console.error(
        "Failed to query appointment file references:",
        fileRefsResponse.errors.map((e) => e.message).join(", "),
      );
    }

    // Delete the uploaded file record
    const response = await this.client.graphql({
      query: deleteUploadedFileReturningID,
      variables: {
        input: { id },
      },
    });

    if (response.errors) {
      throw new Error(
        `Failed to delete file: ${response.errors.map((e) => e.message).join(", ")}`,
      );
    }
  }

  /**
   * Retrieves all uploaded files for an appointment using AppointmentFileReference.
   *
   * This method queries the AppointmentFileReference table to find all files
   * associated with the appointment, then fetches the full UploadedFile details
   * for each reference. The result includes both the file data and the reference
   * status (ORIGINAL, INHERITED_WRITEABLE, or INHERITED_READ_ONLY).
   *
   * @param appointmentId - The ID of the appointment to get files for
   * @returns Array of uploaded files with their reference status
   */
  async getFilesByAppointmentFileReference(
    appointmentId: string,
  ): Promise<UploadedFileWithReferenceStatus[]> {
    const allFiles: UploadedFileWithReferenceStatus[] = [];
    let nextToken: string | null | undefined = undefined;

    // Query all file references for this appointment with pagination
    do {
      const response: {
        data?: AppointmentFileReferencesByAppointmentIDQuery;
        errors?: Array<{ message: string }>;
      } = await this.client.graphql({
        query: appointmentFileReferencesByAppointmentID,
        variables: {
          appointmentID: appointmentId,
          limit: 100,
          nextToken,
        },
      });

      if (response.errors) {
        throw new Error(
          `Failed to query file references: ${response.errors.map((e) => e.message).join(", ")}`,
        );
      }

      const items =
        response.data?.appointmentFileReferencesByAppointmentID?.items ?? [];
      const validRefs = items.filter(
        (item): item is NonNullable<typeof item> =>
          item?.uploadedFileID != null,
      );

      // Fetch file details for each reference in parallel
      const fileResults = await Promise.all(
        validRefs.map(
          async (ref): Promise<UploadedFileWithReferenceStatus | null> => {
            const fileResponse: {
              data?: GetUploadedFileQuery;
              errors?: Array<{ message: string }>;
            } = await this.client.graphql({
              query: getUploadedFileOptimized,
              variables: { id: ref.uploadedFileID },
            });

            if (fileResponse.errors) {
              console.error(
                `Failed to fetch uploaded file ${ref.uploadedFileID}:`,
                fileResponse.errors.map((e) => e.message).join(", "),
              );
              return null;
            }

            const file = fileResponse.data?.getUploadedFile;
            if (!file) {
              console.error(
                `Uploaded file ${ref.uploadedFileID} not found for reference ${ref.id}`,
              );
              return null;
            }

            return {
              id: file.id,
              key: file.key ?? null,
              fileName: file.fileName ?? null,
              type: (file.type as FileType | null) ?? null,
              provider: (file.provider as FileProvider | null) ?? null,
              uploadedBy: file.uploadedBy ?? null,
              uploadedAt: file.uploadedAt ?? null,
              fileSizeBytes: file.fileSizeBytes ?? null,
              sourceAppointmentId: file.sourceAppointmentID ?? null,
              referenceStatus:
                (ref.status as AppointmentFileReferenceStatus) ??
                AppointmentFileReferenceStatus.ORIGINAL,
              referenceId: ref.id,
            };
          },
        ),
      );

      // Filter out null results from failed fetches
      const validFiles = fileResults.filter(
        (file): file is UploadedFileWithReferenceStatus => file !== null,
      );
      allFiles.push(...validFiles);

      nextToken =
        response.data?.appointmentFileReferencesByAppointmentID?.nextToken;
    } while (nextToken);

    return allFiles;
  }

  /**
   * Retrieves all uploaded files for an appointment using AppointmentFileReference.
   *
   * @param params - Parameters including appointmentId
   * @returns Array of uploaded files with their reference status
   */
  async getFilesByAppointment(
    params: GetFilesByAppointmentParams,
  ): Promise<UploadedFileWithReferenceStatus[]> {
    return this.getFilesByAppointmentFileReference(params.appointmentId);
  }

  /**
   * Reports a file inheritance error to console and Sentry.
   */
  private reportFileInheritanceError(
    message: string,
    extra: Record<string, string>,
  ): void {
    console.error(message);
    errorReporter.reportMessage(message, {
      level: "error",
      tags: { component: "file-inheritance" },
      extra,
    });
  }

  /**
   * Creates a single AppointmentFileReference record.
   * @returns true if successful, false if failed
   */
  private async createFileReference(
    appointmentId: string,
    uploadedFileId: string,
    status: AppointmentFileReferenceStatus,
    errorContext: Record<string, string>,
  ): Promise<boolean> {
    const response = await this.client.graphql({
      query: createAppointmentFileReference,
      variables: {
        input: {
          appointmentID: appointmentId,
          uploadedFileID: uploadedFileId,
          status,
        },
      },
    });

    if (response.errors) {
      this.reportFileInheritanceError(
        `Failed to create inherited file reference for file ${uploadedFileId}: ${response.errors.map((e) => e.message).join(", ")}`,
        { ...errorContext, uploadedFileID: uploadedFileId },
      );
      return false;
    }
    return true;
  }

  /**
   * Fetches the FileType for a given uploaded file ID.
   * Returns null if the file cannot be fetched.
   */
  private async getFileType(uploadedFileId: string): Promise<FileType | null> {
    try {
      const response: {
        data?: GetUploadedFileQuery;
        errors?: Array<{ message: string }>;
      } = await this.client.graphql({
        query: getUploadedFileOptimized,
        variables: { id: uploadedFileId },
      });

      if (response.errors) {
        console.error(
          `Failed to fetch file type for ${uploadedFileId}:`,
          response.errors.map((e) => e.message).join(", "),
        );
        return null;
      }

      return (response.data?.getUploadedFile?.type as FileType | null) ?? null;
    } catch (error) {
      console.error(`Failed to fetch file type for ${uploadedFileId}:`, error);
      return null;
    }
  }

  /**
   * Creates all inherited file references for a child appointment.
   *
   * Queries the source appointment's file references and creates inherited refs
   * for the destination appointment using getInheritedFileStatus.
   *
   * @returns The total number of file references created
   */
  async createInheritedFileReferences(
    sourceAppointmentId: string,
    destAppointmentId: string,
    destAppointmentType: AppointmentType,
    isReschedule: boolean,
  ): Promise<number> {
    let totalCreated = 0;

    // Query and process source appointment's file references
    let nextToken: string | null | undefined = undefined;
    do {
      const response: {
        data?: AppointmentFileReferencesByAppointmentIDQuery;
        errors?: Array<{ message: string }>;
      } = await this.client.graphql({
        query: appointmentFileReferencesByAppointmentID,
        variables: {
          appointmentID: sourceAppointmentId,
          limit: 100,
          nextToken,
        },
      });

      if (response.errors) {
        this.reportFileInheritanceError(
          `Failed to query file references for source appointment: ${response.errors.map((e) => e.message).join(", ")}`,
          { sourceAppointmentId, destAppointmentId },
        );
        break;
      }

      const items =
        response.data?.appointmentFileReferencesByAppointmentID?.items ?? [];
      const results = await Promise.all(
        items
          .filter(
            (item): item is NonNullable<typeof item> =>
              item?.uploadedFileID != null,
          )
          .map(async (item) => {
            const fileType = await this.getFileType(item.uploadedFileID);
            const status = getInheritedFileStatus({
              isReschedule,
              destAppointmentType,
              fileType: fileType ?? FileType.UNDETERMINED,
              sourceFileStatus:
                (item.status as AppointmentFileReferenceStatus) ??
                AppointmentFileReferenceStatus.ORIGINAL,
            });
            return this.createFileReference(
              destAppointmentId,
              item.uploadedFileID,
              status,
              {
                sourceAppointmentId,
                destAppointmentId,
              },
            );
          }),
      );
      totalCreated += results.filter(Boolean).length;
      nextToken =
        response.data?.appointmentFileReferencesByAppointmentID?.nextToken;
    } while (nextToken);

    return totalCreated;
  }

  /**
   * Gets files that should be deleted when an appointment is deleted.
   *
   * This method finds all files referenced by the appointment and checks if
   * each file has any other references. Files that are only referenced by
   * this appointment should be deleted along with the appointment.
   *
   * @param appointmentId - The ID of the appointment being deleted
   * @returns Object containing files to delete and their reference IDs
   */
  async getFilesToDeleteForAppointment(appointmentId: string): Promise<{
    /** Files that should be deleted (only referenced by this appointment) */
    filesToDelete: Array<{
      id: string;
      key: string | null;
      referenceId: string;
    }>;
    /** File references to delete (all references for this appointment) */
    referenceIdsToDelete: string[];
  }> {
    const filesToDelete: Array<{
      id: string;
      key: string | null;
      referenceId: string;
    }> = [];
    const referenceIdsToDelete: string[] = [];

    // Get all file references for this appointment
    const files = await this.getFilesByAppointmentFileReference(appointmentId);

    // For each file, check if there are other references
    for (const file of files) {
      referenceIdsToDelete.push(file.referenceId);

      // Query all references for this file
      const refsResponse = await this.client.graphql({
        query: appointmentFileReferencesByUploadedFileID,
        variables: {
          uploadedFileID: file.id,
        },
      });

      if (refsResponse.errors) {
        console.error(
          `Failed to query references for file ${file.id}:`,
          refsResponse.errors.map((e) => e.message).join(", "),
        );
        continue;
      }

      const allRefs =
        refsResponse.data?.appointmentFileReferencesByUploadedFileID?.items ??
        [];
      const validRefs = allRefs.filter(
        (ref): ref is NonNullable<typeof ref> => ref?.id != null,
      );

      // If this is the only reference, the file should be deleted
      if (validRefs.length === 1) {
        filesToDelete.push({
          id: file.id,
          key: file.key,
          referenceId: file.referenceId,
        });
      }
    }

    return { filesToDelete, referenceIdsToDelete };
  }

  /**
   * Deletes an AppointmentFileReference record by ID.
   *
   * @param referenceId - The ID of the reference to delete
   */
  async deleteFileReference(referenceId: string): Promise<void> {
    const response = await this.client.graphql({
      query: deleteAppointmentFileReference,
      variables: {
        input: { id: referenceId },
      },
    });

    if (response.errors) {
      throw new Error(
        `Failed to delete file reference: ${response.errors.map((e) => e.message).join(", ")}`,
      );
    }
  }

  // --------------------------------------------------------------------
  // HistoryTranscript side-table
  // --------------------------------------------------------------------
  // Every UploadedFile of type HISTORY_TRANSCRIPT has a companion
  // HistoryTranscript row (keyed by uploadedFileID) used to track call
  // metadata (callId, callStartTime) once Zoom webhook ingestion lands.
  // Manual uploads leave those fields null.
  //
  // Side-table writes never throw — the UploadedFile is the authoritative
  // row, and an orphaned or missing side-table entry can always be fixed
  // by toggling the type. Failures are logged at console.error only, to
  // match the pattern used for AppointmentFileReference sibling writes
  // elsewhere in this service.

  private async createHistoryTranscriptRow(
    uploadedFileID: string,
  ): Promise<void> {
    try {
      const response = await this.client.graphql({
        query: createHistoryTranscript,
        variables: { input: { uploadedFileID } },
      });
      if (response.errors) {
        console.error(
          `Failed to create HistoryTranscript row for ${uploadedFileID}:`,
          response.errors.map((e) => e.message).join(", "),
        );
      }
    } catch (error) {
      console.error(
        `Failed to create HistoryTranscript row for ${uploadedFileID}:`,
        error,
      );
    }
  }

  private async findHistoryTranscriptIds(
    uploadedFileID: string,
  ): Promise<string[]> {
    try {
      const response: {
        data?: HistoryTranscriptsByUploadedFileIDQuery;
        errors?: Array<{ message: string }>;
      } = await this.client.graphql({
        query: historyTranscriptsByUploadedFileID,
        variables: { uploadedFileID },
      });
      if (response.errors) {
        console.error(
          `Failed to query HistoryTranscript rows for ${uploadedFileID}:`,
          response.errors.map((e) => e.message).join(", "),
        );
        return [];
      }
      const items =
        response.data?.historyTranscriptsByUploadedFileID?.items ?? [];
      return items
        .filter((i): i is NonNullable<typeof i> => i?.id != null)
        .map((i) => i.id);
    } catch (error) {
      console.error(
        `Failed to query HistoryTranscript rows for ${uploadedFileID}:`,
        error,
      );
      return [];
    }
  }

  private async deleteHistoryTranscriptRowsByIds(ids: string[]): Promise<void> {
    await Promise.all(
      ids.map(async (id) => {
        try {
          const response = await this.client.graphql({
            query: deleteHistoryTranscript,
            variables: { input: { id } },
          });
          if (response.errors) {
            console.error(
              `Failed to delete HistoryTranscript row ${id}:`,
              response.errors.map((e) => e.message).join(", "),
            );
          }
        } catch (error) {
          console.error(`Failed to delete HistoryTranscript row ${id}:`, error);
        }
      }),
    );
  }

  /**
   * Make the HistoryTranscript side-table mirror the UploadedFile.type
   * after a type change. Idempotent — a user can flip the dropdown any
   * number of times and the row will end up matching the final type.
   */
  private async syncHistoryTranscriptRow(
    uploadedFileID: string,
    newType: FileType,
  ): Promise<void> {
    const existingIds = await this.findHistoryTranscriptIds(uploadedFileID);
    if (newType === FileType.HISTORY_TRANSCRIPT) {
      if (existingIds.length === 0) {
        await this.createHistoryTranscriptRow(uploadedFileID);
      }
    } else {
      await this.deleteHistoryTranscriptRowsByIds(existingIds);
    }
  }
}

export const uploadedFileService = UploadedFileService.getInstance();
